import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import isEqual from 'lodash/isEqual';
import { useCallback, useState } from 'react';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { CloseIcon } from '../../icons/closeIcon';
import { cancelPendingAvatarChanges, setAvatarPartIndex, setIsAvatarEditorOpen, thunkSaveAvatar } from '../../redux/actions/selfAvatarActions';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { SELF_PANEL_BACKGROUND_COLOR } from '../../styles/colors';
import { HUMAN_AVATAR_PART_DEFINITIONS } from '../../types/avatarTypes';
import { AvatarGeneralControlsContainer } from './avatarGeneralControlsContainer';
import { AvatarPartBrowser } from './avatarPartBrowser';
import { AVATAR_PART_BUTTON_CSS } from './avatarPartButton';
const FINISH_EDITING_BUTTONS_CONTAINER_CSS = css `
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 5px;
  gap: 10px;
  background-color: ${SELF_PANEL_BACKGROUND_COLOR};
`;
const PARTS_CONTAINER_CSS = css `
  display: flex;
  flex-flow: row wrap;
  gap: 2px;
  padding: 5px;
`;
export const AvatarEditor = () => {
    const dispatch = useAppDispatch();
    const socketSend = useSocketSend();
    const playerAvatar = useAppSelector((state) => state.selfAvatar.avatar?.savedAvatar);
    const pendingPlayerAvatar = useAppSelector((state) => state.selfAvatar.avatar?.pendingAvatar);
    const [browsingPartType, setBrowsingPartType] = useState();
    const onClickSave = useCallback(() => {
        dispatch(thunkSaveAvatar(socketSend));
    }, [dispatch, socketSend]);
    const onClickClose = useCallback(() => {
        dispatch(setIsAvatarEditorOpen(false));
        dispatch(cancelPendingAvatarChanges());
    }, [dispatch]);
    const onClickBrowsePart = useCallback((partType) => {
        setBrowsingPartType(partType);
    }, []);
    const setPartIndex = useCallback((partType, partIndex, subType) => {
        dispatch(setAvatarPartIndex(partType, partIndex, subType));
        setBrowsingPartType(undefined);
    }, [dispatch]);
    const cancelBrowsing = useCallback(() => {
        setBrowsingPartType(undefined);
    }, []);
    if (!playerAvatar ||
        playerAvatar.type !== 'custom' ||
        !pendingPlayerAvatar ||
        pendingPlayerAvatar.type !== 'custom') {
        return null;
    }
    const hasChanged = !isEqual(playerAvatar.avatar, pendingPlayerAvatar.avatar);
    if (browsingPartType) {
        return (_jsx(AvatarPartBrowser, { partType: browsingPartType, onClickPartIndex: (partIndex, subType) => {
                setPartIndex(browsingPartType, partIndex, subType);
            }, onClickBack: cancelBrowsing }));
    }
    return (_jsxs("section", { "aria-label": 'avatar editor', children: [_jsxs("div", { css: FINISH_EDITING_BUTTONS_CONTAINER_CSS, children: [_jsx(Button, { variant: 'contained', color: hasChanged ? 'secondary' : 'primary', disabled: !hasChanged, onClick: onClickSave, startIcon: _jsx(CheckCircleOutlineRoundedIcon, {}), size: 'large', children: "Save" }), _jsx(Button, { onClick: onClickClose, variant: 'contained', startIcon: _jsx(CloseIcon, {}), size: 'large', children: hasChanged ? 'Cancel' : 'Close' })] }), _jsx(AvatarGeneralControlsContainer, {}), _jsx("div", { css: PARTS_CONTAINER_CSS, children: Object.keys(HUMAN_AVATAR_PART_DEFINITIONS).map((partType) => {
                    const { label, isDisabled } = HUMAN_AVATAR_PART_DEFINITIONS[partType];
                    if (isDisabled) {
                        return null;
                    }
                    return (_jsx("button", { css: AVATAR_PART_BUTTON_CSS, onClick: () => {
                            onClickBrowsePart(partType);
                        }, children: _jsx(Typography, { children: label }) }, partType));
                }) })] }));
};
